import { makeAutoObservable, runInAction } from 'mobx';
import type { RootStore } from '../root-store';
import { CreateImporterDto, ImporterDto, ImportTypeEnum, UpdateImporterDto } from '../../../defs/api';

export type ImporterDialogMode = 'create' | 'update';

export interface ImporterFormValues {
    id: ImportTypeEnum | '';
    description: string;
    blacklist: string;
    isEnabled: boolean;
}

export class AdminImporterStore {
    rootStore: RootStore;

    importers: ImporterDto[] = [];

    editedImporter: ImporterDto | null = null;

    dialogMode: ImporterDialogMode = 'create';

    dialogOpened = false;

    loading = false;

    deleteInProgress = false;

    createOrUpdateErrorMessage: string | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, {
            rootStore: false,
        });
    }

    async fetchImporters(): Promise<void> {
        try {
            this.setLoading(true);
            const importers = await this.rootStore.apiClient.importerController.importerControllerGetList();

            runInAction(() => {
                this.importers = importers;
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.setLoading(false);
        }
    }

    async create(input: CreateImporterDto): Promise<void> {
        this.createOrUpdateErrorMessage = null;
        try {
            this.setLoading(true);
            await this.rootStore.apiClient.importerController.importerControllerCreate({
                createImporterDto: input,
            });
            await this.fetchImporters();
            this.invalidateImporterCache();
            this.closeDialog();
        } catch (e) {
            runInAction(() => {
                this.createOrUpdateErrorMessage = e.message;
            });
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            this.setLoading(false);
        }
    }

    async update(input: UpdateImporterDto): Promise<void> {
        if (!this.editedImporter?.id) return;

        this.createOrUpdateErrorMessage = null;
        try {
            this.setLoading(true);
            await this.rootStore.apiClient.importerController.importerControllerUpdate({
                id: this.editedImporter.id,
                updateImporterDto: input,
            });
            await this.fetchImporters();
            this.invalidateImporterCache();
            this.closeDialog();
        } catch (e) {
            runInAction(() => {
                this.createOrUpdateErrorMessage = e.message;
            });
            // eslint-disable-next-line no-console
            console.error(e);
        } finally {
            this.setLoading(false);
        }
    }

    async delete(id: ImportTypeEnum): Promise<void> {
        this.setDeleteInProgress(true);
        try {
            const deleted = await this.rootStore.apiClient.importerController.importerControllerDelete({ id });

            if (deleted) {
                await this.fetchImporters();
                this.invalidateImporterCache();
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.setDeleteInProgress(false);
        }
    }

    openCreateDialog(): void {
        this.dialogMode = 'create';
        this.editedImporter = null;
        this.dialogOpened = true;
        this.rootStore.alertStore.setModalPlacement();
    }

    openUpdateDialog(importer: ImporterDto): void {
        this.dialogMode = 'update';
        this.editedImporter = importer;
        this.dialogOpened = true;
        this.rootStore.alertStore.setModalPlacement();
    }

    closeDialog(): void {
        this.dialogOpened = false;
        this.editedImporter = null;
        this.createOrUpdateErrorMessage = null;
        this.rootStore.alertStore.setDefaultPlacement();
    }

    setLoading(loading: boolean): void {
        this.loading = loading;
    }

    setDeleteInProgress(deleteInProgress: boolean): void {
        this.deleteInProgress = deleteInProgress;
    }

    invalidateImporterCache(): void {
        this.rootStore.importerStore.importers = null;
    }

    get hasImporters(): boolean {
        return this.importers.length > 0;
    }

    get usedImporterIds(): ImportTypeEnum[] {
        return this.importers.map((importer) => importer.id).filter((id): id is ImportTypeEnum => Boolean(id));
    }

    get availableImportTypes(): ImportTypeEnum[] {
        return Object.values(ImportTypeEnum).filter((importType) => !this.usedImporterIds.includes(importType));
    }

    get hasAvailableImportTypes(): boolean {
        return this.availableImportTypes.length > 0;
    }

    get initialDialogValues(): ImporterFormValues {
        if (this.dialogMode === 'update' && this.editedImporter) {
            return {
                id: this.editedImporter.id || '',
                description: this.editedImporter.description || '',
                blacklist: (this.editedImporter.blacklist || []).join(';'),
                isEnabled: this.editedImporter.isEnabled,
            };
        }

        return {
            id: this.availableImportTypes[0] || '',
            description: '',
            blacklist: '',
            isEnabled: true,
        };
    }
}
